// Limited Access UI - registry of what a limited user's sidebar can show.
//
// A page only appears in a limited user's sidebar when `rolledOut` is true AND that user's
// own permission allows it. `kind: 'route'` pages navigate the sidebar's own router; `kind:
// 'modal'` pages open a global overlay instead (LimitedShell renders it), matching how these
// features already behave in the full dashboard (Quotes/Pro Forma/Tools are dropdown+modal
// there too, never their own page). To roll another page out later: add an entry here, and
// either a matching <Route> in LimitedRoutes.jsx (route kind) or a case in LimitedShell's
// modal renderer (modal kind).
//
// Deliberately NOT included:
//   - "Files" (can_access_files) - removed app-wide per GDPR, no feature left to link to.
//   - "CRM Manager" (is_crm_manager) - a behaviour flag on Manage Calls / Sales Hub, not a
//     page of its own, and neither of those pages are in the Limited UI's scope yet.
import { FileQuestion, FileText, ClipboardList, Wrench, CalendarClock } from 'lucide-react';

export const LIMITED_PAGES = [
  {
    id: 'stock-transfer',
    label: 'Stock Transfer',
    kind: 'route',
    route: '/stock-transfer',
    artKey: 'stock-transfer',
    rolledOut: true,
    canAccess: (user) => !!user && (user.can_access_stock_transfer !== false || user.role === 'super_admin'),
  },
  {
    id: 'transport',
    label: 'Transport',
    kind: 'route',
    route: '/transport',
    artKey: 'transport',
    rolledOut: true,
    canAccess: (user) => !!user && (!!user.can_access_transport || user.role === 'super_admin'),
  },
  {
    id: 'pi-counts-warehouse',
    label: 'PI Counts W/H',
    kind: 'route',
    route: '/pi-counts-warehouse',
    artKey: 'pi-counts-wh',
    rolledOut: true,
    canAccess: (user) => !!user && (!!user.can_view_pi_warehouse || user.role === 'super_admin'),
  },
  {
    id: 'goods-in-warehouse',
    label: 'Goods In W/H',
    kind: 'route',
    route: '/goods-in-warehouse',
    artKey: 'goods-in-wh',
    rolledOut: true,
    canAccess: (user) => !!user && (!!user.can_access_goods_in_warehouse || user.role === 'super_admin'),
  },
  {
    id: 'po-expenses',
    label: 'P.O. Expenses',
    kind: 'route',
    route: '/po-expenses',
    icon: ClipboardList,
    rolledOut: true,
    canAccess: (user) => !!user && (user.can_access_po_expenses !== false || user.role === 'super_admin'),
  },
  {
    id: 'annual-leave',
    label: 'Annual Leave',
    kind: 'route',
    route: '/group-absence-calendar',
    icon: CalendarClock,
    rolledOut: true,
    canAccess: (user) => !!user && (!!user.can_access_al_button || user.role === 'super_admin'),
  },
  {
    id: 'quotes',
    label: 'Quotes',
    kind: 'modal',
    modalKey: 'quotes',
    icon: FileQuestion,
    rolledOut: true,
    canAccess: (user) => !!user && (user.can_access_quotes !== false || user.role === 'super_admin'),
  },
  {
    id: 'proforma',
    label: 'Pro Forma',
    kind: 'modal',
    modalKey: 'proforma',
    icon: FileText,
    rolledOut: true,
    canAccess: (user) => !!user && (user.can_access_proforma !== false || user.role === 'super_admin'),
  },
  {
    id: 'tools',
    label: 'Tools',
    kind: 'modal',
    modalKey: 'tools',
    icon: Wrench,
    rolledOut: true,
    canAccess: (user) => !!user && (!!user.can_access_tools || user.role === 'super_admin'),
  },
];

// The CRM and Staffology links from the Home page cards (same targets Hub.jsx / CRMPortal.jsx use).
export const LIMITED_EXTERNAL_LINKS = [
  {
    id: 'crm',
    label: 'CRM',
    artKey: 'crm',
    url: 'https://crm.zoho.eu/crm/encon/tab/Home/begin',
    canAccess: (user) => !!user && (!!user.can_access_crm || user.role === 'super_admin'),
  },
  {
    id: 'staffology',
    label: 'Staffology',
    artKey: 'staffology',
    url: 'https://app.staffologyhr.co.uk/portal/logoff.aspx',
    canAccess: (user) => !!user && (!!user.can_access_staffology || user.role === 'super_admin'),
  },
];

export const getVisibleLimitedPages = (user) =>
  LIMITED_PAGES.filter((page) => page.rolledOut && page.canAccess(user));

export const getVisibleExternalLinks = (user) =>
  LIMITED_EXTERNAL_LINKS.filter((link) => link.canAccess(user));

export const isRouteActive = (pathname, route) =>
  pathname === route || pathname.startsWith(`${route}/`);

// Quick links open in a new tab when they are full URLs, otherwise they are in-app routes.
export const isExternalUrl = (url) => /^https?:\/\//i.test(url || '');
